import { ImageIcon, WarningIcon, DownloadSimpleIcon, XIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import {
  generatedImageAlt,
  generatedImageCaption,
  generatedImageSrc,
  type GeneratedImageOutput,
} from './image-source';

// FRONT-1423: re-exported so existing importers (e.g. message-bubble.tsx)
// are untouched — the type itself now lives in the dependency-free
// `image-source.ts`, which the phone surface also imports.
export type { GeneratedImageOutput };

/**
 * Specialised render for `generate_image` (FRONT-535). Shows the generated
 * image inline (served from the files API) with the model + prompt caption and
 * a download link. Falls back to a plain error row when generation failed.
 */
export function GeneratedImageCard({ output }: { output: GeneratedImageOutput }) {
  const src = generatedImageSrc(output);

  if (output.error || !src) {
    return (
      <div className="flex items-center gap-2 text-xs text-destructive px-3 py-2 border border-destructive/20 rounded-lg">
        <WarningIcon size={14} />
        <span>{output.error ?? 'Image generation failed.'}</span>
      </div>
    );
  }

  const caption = generatedImageCaption(output);
  const altText = generatedImageAlt(output);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card max-w-[420px]">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <ImageIcon size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium text-foreground truncate">Generated image</span>
        {output.model && (
          <span className="ml-auto font-mono text-[length:var(--text-2xs)] text-muted-foreground truncate">
            {output.model}
          </span>
        )}
      </div>
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className="group block w-full cursor-pointer">
            <img
              src={src}
              alt={altText}
              loading="lazy"
              className="block w-full h-auto bg-muted/30 outline outline-1 -outline-offset-1 outline-border transition-opacity group-hover:opacity-90"
            />
          </button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-fit sm:max-w-[calc(100%-2rem)] gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Generated image preview</DialogTitle>
          </DialogHeader>
          <div className="relative flex h-[82vh] max-h-[720px] w-[88vw] max-w-[960px] items-center justify-center bg-muted/30">
            <img
              src={src}
              alt={altText}
              className="h-full w-full object-contain outline outline-1 -outline-offset-1 outline-border"
            />
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon-lg"
                className="absolute top-2 right-2 rounded-full bg-black/50 text-white transition-[background-color,transform] hover:bg-black/70 hover:text-white active:scale-[0.96]"
              >
                <XIcon size={16} />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>
          </div>
          {caption && (
            <div className="px-3 py-2 text-xs text-muted-foreground max-h-[15vh] overflow-y-auto">
              <p>{caption}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {caption && (
        <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
          <p className="line-clamp-3">{caption}</p>
        </div>
      )}
      <div className="px-3 py-2 border-t border-border">
        <a
          href={src}
          download
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          <DownloadSimpleIcon size={12} className="shrink-0" />
          Download
        </a>
      </div>
    </div>
  );
}
